import { CheckCircle2, CircleAlert, History, LoaderCircle, RotateCcw, UserRound } from 'lucide-react';
import type { TelephonyRoutePreview, TelephonyRoutingHistoryEntry } from '../types';
import { RoutingDiffView } from './RoutingDiffView';

type RoutingHistoryPanelProps = {
  entries: TelephonyRoutingHistoryEntry[];
  isLoading: boolean;
  error: string | null;
  memberNames: ReadonlyMap<string, string>;
};

const OUTCOME_LABELS: Record<TelephonyRoutingHistoryEntry['outcome'], string> = {
  APPLIED: 'Применено успешно',
  ROLLED_BACK: 'Выполнен автоматический откат',
  ROLLBACK_FAILED: 'Откат требует проверки',
};

function formatHistoryDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
}

function isLegacyEntry(entry: TelephonyRoutingHistoryEntry) {
  return entry.previousRevision === null || entry.attemptedRevision === null;
}

function historyComparison(entry: TelephonyRoutingHistoryEntry): {
  preview: TelephonyRoutePreview;
  beforeTitle: string;
  afterTitle: string;
} | null {
  if (entry.outcome === 'APPLIED') {
    if (!entry.attemptedSnapshot) return null;
    return {
      preview: {
        snapshot: entry.attemptedSnapshot,
        appliedSnapshot: entry.beforeSnapshot,
        validation: { errors: [], warnings: [], chain: [] },
        technical: null,
        draftRevision: entry.attemptedRevision ?? 0,
        appliedRevision: entry.previousRevision ?? 0,
      },
      beforeTitle: 'До применения',
      afterTitle: 'Применённая схема',
    };
  }

  const restoredSnapshot = entry.restoredSnapshot || entry.beforeSnapshot;
  if (!entry.attemptedSnapshot || !restoredSnapshot) return null;
  return {
    preview: {
      snapshot: restoredSnapshot,
      appliedSnapshot: entry.attemptedSnapshot,
      validation: { errors: [], warnings: [], chain: [] },
      technical: null,
      draftRevision: entry.previousRevision ?? 0,
      appliedRevision: entry.attemptedRevision ?? 0,
    },
    beforeTitle: 'Неудачная попытка',
    afterTitle: 'Восстановленная схема',
  };
}

function OutcomeIcon({ outcome }: { outcome: TelephonyRoutingHistoryEntry['outcome'] }) {
  if (outcome === 'APPLIED') return <CheckCircle2 />;
  if (outcome === 'ROLLED_BACK') return <RotateCcw />;
  return <CircleAlert />;
}

export function RoutingHistoryPanel({ entries, isLoading, error, memberNames }: RoutingHistoryPanelProps) {
  return <section className="surface telephony-routing-history">
    <div className="telephony-route-section-head">
      <div><small>ЖУРНАЛ ИЗМЕНЕНИЙ</small><h3><History /> История применений</h3><p>Неизменяемые результаты применения и автоматического отката рабочих маршрутов.</p></div>
      {!isLoading && !error && <span className="telephony-routing-history-count">{entries.length}</span>}
    </div>

    {isLoading && <div className="telephony-routing-history-state loading">
      <LoaderCircle className="spin" />
      <span><b>Загружаем историю</b><small>Получаем последние операции маршрутизации.</small></span>
    </div>}

    {!isLoading && error && <div className="telephony-routing-history-state error" role="alert">
      <CircleAlert />
      <span><b>Не удалось загрузить историю</b><small>{error}</small></span>
    </div>}

    {!isLoading && !error && !entries.length && <div className="telephony-routing-history-state empty">
      <History />
      <span><b>Применений пока не было</b><small>Первая запись появится после успешного применения или автоматического отката.</small></span>
    </div>}

    {!isLoading && !error && entries.length > 0 && <div className="telephony-routing-history-list">
      {entries.map((entry, index) => {
        const comparison = historyComparison(entry);
        const legacy = isLegacyEntry(entry);
        return <details className={`telephony-routing-history-entry ${entry.outcome.toLowerCase()}`} key={entry.id} open={index === 0}>
          <summary>
            <span className="telephony-routing-history-icon"><OutcomeIcon outcome={entry.outcome} /></span>
            <span className="telephony-routing-history-title">
              <b>{OUTCOME_LABELS[entry.outcome]}</b>
              <small>{entry.message}</small>
            </span>
            <span className="telephony-routing-history-meta">
              <time dateTime={entry.createdAt}>{formatHistoryDate(entry.createdAt)}</time>
              <em><UserRound /> {entry.actorName}</em>
            </span>
            <span className="telephony-routing-history-badges">
              {entry.attemptedRevision !== null && <i>r{entry.attemptedRevision}</i>}
              {legacy && <i className="legacy">Старая запись</i>}
            </span>
          </summary>
          <div className="telephony-routing-history-body">
            {comparison ? <RoutingDiffView
              preview={comparison.preview}
              memberNames={memberNames}
              historical
              beforeTitle={comparison.beforeTitle}
              afterTitle={comparison.afterTitle}
              compact
            /> : <div className="telephony-routing-history-no-snapshot">
              <History />
              <span><b>Снимки маршрута недоступны</b><small>Событие сохранено в старом формате; результат и время операции остаются достоверными.</small></span>
            </div>}
          </div>
        </details>;
      })}
    </div>}
  </section>;
}
